import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { InheritableField } from './inheritable-field'
import { AdvancedParamsSection } from './advanced-params-section'
import { ModelSpecificSection } from './model-specific-section'
import {
  isWrappedNodeConfig,
  migrateNodeConfigToInheritanceBlob,
  type LLMNodeConfig,
} from '@/schemas/workflow-node'
import {
  resolveEffectiveLlmConfig,
  type EffectiveLLMConfig,
} from '@/lib/resolve-effective-llm-config'
import { useLLMModelsStore } from '@/stores/llm-models-store'
import { useWorkflowStore } from '@/stores/workflow-store'
import type { LLMProviderValue } from '@/schemas/llm-api-key'

/**
 * Phase 4 plan 04-05 Task 2D — node-side configuration panel for LLM nodes.
 *
 * Composes InheritableField primitives for model / temperature / max_tokens,
 * and renders AdvancedParamsSection + ModelSpecificSection. On mount the panel
 * ensures the incoming config is in the wrapped Phase-4 shape, migrating flat
 * Phase-2 configs via `migrateNodeConfigToInheritanceBlob`. Effective values
 * displayed in the inherited state come from `resolveEffectiveLlmConfig`
 * applied against the current workflow's `settings.default_llm` (D-05).
 *
 * Matches the existing `NodeFormHandle` contract from flow/node-config-panel
 * so the parent Sheet's footer Save button can trigger `submit()`.
 */

export interface NodeFormHandle {
  submit: () => void
}

export interface LlmNodeConfigPanelProps {
  /** Raw config blob from the selected flow node (may be flat or wrapped). */
  defaultValues: Record<string, unknown>
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

// Per-provider capability whitelist for the Advanced sub-form (RESEARCH §3).
// Hidden = the provider does not accept the param at all; the resolver will
// drop these from the payload regardless, but we hide them to avoid confusion.
const ADVANCED_ALLOWED: Record<LLMProviderValue, Partial<Record<'top_p' | 'frequency_penalty' | 'presence_penalty' | 'stop' | 'seed', boolean>>> = {
  openai: {
    top_p: true,
    frequency_penalty: true,
    presence_penalty: true,
    stop: true,
    seed: true,
  },
  anthropic: {
    top_p: true,
    frequency_penalty: false,
    presence_penalty: false,
    stop: true,
    seed: false,
  },
  google: {
    top_p: true,
    frequency_penalty: false,
    presence_penalty: false,
    stop: true,
    seed: false,
  },
}

export const LlmNodeConfigPanel = forwardRef<NodeFormHandle, LlmNodeConfigPanelProps>(
  function LlmNodeConfigPanel({ defaultValues, onSubmit, disabled }, ref) {
    const { t } = useTranslation('workflows')
    const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow)

    const catalog = useLLMModelsStore((s) => s.catalog)
    const loadCatalog = useLLMModelsStore((s) => s.loadCatalog)

    // --- State: wrapped config blob + non-inheritable sub-blobs ---
    const wrapped: LLMNodeConfig = useMemo(() => {
      if (isWrappedNodeConfig(defaultValues)) return defaultValues
      return migrateNodeConfigToInheritanceBlob(defaultValues)
    }, [defaultValues])

    const [config, setConfig] = useState<LLMNodeConfig>(wrapped)
    const [systemPrompt, setSystemPrompt] = useState<string>(
      (defaultValues.system_prompt as string | undefined) ?? ''
    )
    const [userPromptTemplate, setUserPromptTemplate] = useState<string>(
      (defaultValues.user_prompt_template as string | undefined) ?? ''
    )

    // Effective (resolved) values drive the "inheritedValue" displayed when
    // a field is inherited (RESEARCH §5.2).
    const effective: EffectiveLLMConfig = useMemo(() => {
      const workflowSettings =
        (currentWorkflow?.settings as Record<string, unknown> | null) ?? {}
      return resolveEffectiveLlmConfig(
        config as unknown as Record<string, unknown>,
        workflowSettings
      )
    }, [config, currentWorkflow])

    // Provider derived from effective config (inherited or node override).
    const provider = effective.provider as LLMProviderValue

    // Load catalog when provider changes (5-minute cache inside the store).
    useEffect(() => {
      if (provider) void loadCatalog(provider)
    }, [provider, loadCatalog])

    const modelOptions = catalog[provider] ?? []
    const currentModelEntry = modelOptions.find((m) => m.id === effective.model)
    const capabilities = currentModelEntry?.capabilities ?? []
    const isCustomModel =
      !!effective.model && !modelOptions.some((m) => m.id === effective.model)

    // Reasoning disables temperature (UI-SPEC §Copywriting).
    const reasoningDisablesTemp =
      capabilities.includes('reasoning') ||
      capabilities.includes('reasoning_minimal') ||
      capabilities.includes('reasoning_none')

    useImperativeHandle(
      ref,
      () => ({
        submit: () =>
          onSubmit({
            ...config,
            system_prompt: systemPrompt,
            user_prompt_template: userPromptTemplate,
          } as unknown as Record<string, unknown>),
      }),
      [config, systemPrompt, userPromptTemplate, onSubmit]
    )

    // --- Helpers -----------------------------------------------------------
    const patchTopLevel = <K extends 'model' | 'temperature' | 'max_tokens'>(
      key: K,
      value: LLMNodeConfig[K]
    ) => setConfig((prev) => ({ ...prev, [key]: value }))

    return (
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit({
            ...config,
            system_prompt: systemPrompt,
            user_prompt_template: userPromptTemplate,
          } as unknown as Record<string, unknown>)
        }}
      >
        {/* System prompt / user prompt are NOT inheritable (prompt ownership
            stays with the node per CONTEXT D-01). */}
        <div className="space-y-2">
          <Label>System Prompt</Label>
          <Textarea
            value={systemPrompt}
            disabled={disabled}
            onChange={(e) => setSystemPrompt(e.target.value)}
            className="font-mono min-h-[100px] bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
          />
        </div>
        <div className="space-y-2">
          <Label>User Prompt Template</Label>
          <Textarea
            value={userPromptTemplate}
            disabled={disabled}
            onChange={(e) => setUserPromptTemplate(e.target.value)}
            className="font-mono min-h-[100px] bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
          />
        </div>

        {/* Model (inheritable) */}
        <InheritableField<string>
          label={t('llmConfig.sections.model')}
          inheritedValue={effective.model}
          value={config.model.value}
          inherited={config.model.inherited}
          onChange={(val, isInh) =>
            patchTopLevel('model', {
              inherited: isInh,
              value: isInh ? null : val,
            })
          }
          disabled={disabled}
          renderInput={({ value, onChange, disabled: d }) => (
            modelOptions.length > 0 ? (
              <Select
                value={value}
                disabled={d}
                onValueChange={(v) => onChange(v)}
              >
                <SelectTrigger className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50">
                  <SelectValue
                    placeholder={t('llmConfig.custom.inputPlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {modelOptions.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="font-mono">{m.display_name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={value}
                disabled={d}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('llmConfig.custom.inputPlaceholder')}
                className="font-mono bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              />
            )
          )}
        />

        {/* Temperature (inheritable) — disabled when model is reasoning-class */}
        <div className="space-y-1">
          <InheritableField<number>
            label={t('llmConfig.sections.temperature')}
            inheritedValue={effective.temperature}
            value={config.temperature.value}
            inherited={config.temperature.inherited}
            onChange={(val, isInh) =>
              patchTopLevel('temperature', {
                inherited: isInh,
                value: isInh ? null : val,
              })
            }
            disabled={disabled || reasoningDisablesTemp}
            formatDisplay={(v) => v.toFixed(2)}
            renderInput={({ value, onChange, disabled: d }) => (
              <Input
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={value}
                disabled={d}
                onChange={(e) => onChange(Number(e.target.value))}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              />
            )}
          />
          {reasoningDisablesTemp && (
            <p className="text-xs text-amber-300">
              {t('llmConfig.errors.temperatureDisabledOnReasoning')}
            </p>
          )}
        </div>

        {/* Max tokens (inheritable) */}
        <InheritableField<number>
          label={t('llmConfig.sections.maxTokens')}
          inheritedValue={effective.max_tokens}
          value={config.max_tokens.value}
          inherited={config.max_tokens.inherited}
          onChange={(val, isInh) =>
            patchTopLevel('max_tokens', {
              inherited: isInh,
              value: isInh ? null : val,
            })
          }
          disabled={disabled}
          renderInput={({ value, onChange, disabled: d }) => (
            <Input
              type="number"
              min={1}
              value={value}
              disabled={d}
              onChange={(e) => onChange(Number(e.target.value) || 1)}
              className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            />
          )}
        />

        {/* Advanced (collapsible, provider-aware) */}
        <AdvancedParamsSection
          advanced={config.advanced ?? {}}
          inherited={{
            top_p: effective.top_p ?? undefined,
            frequency_penalty: effective.frequency_penalty ?? undefined,
            presence_penalty: effective.presence_penalty ?? undefined,
            stop: effective.stop ?? undefined,
            seed: effective.seed ?? undefined,
          }}
          allowed={ADVANCED_ALLOWED[provider]}
          disabled={disabled}
          onChange={(next) => setConfig((prev) => ({ ...prev, advanced: next }))}
        />

        {/* Model-specific — conditional sub-forms per capabilities (D-18) */}
        <ModelSpecificSection
          capabilities={capabilities}
          isCustomModel={isCustomModel}
          value={config.model_specific ?? {}}
          maxTokens={effective.max_tokens}
          disabled={disabled}
          onChange={(next) =>
            setConfig((prev) => ({ ...prev, model_specific: next }))
          }
        />
      </form>
    )
  }
)
